import { Check } from 'lucide-react';

export interface Step {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

export interface ProgressStepperProps {
  steps: Step[];
  currentStep: string;
  completedSteps: string[];
}

export function ProgressStepper({ steps, currentStep, completedSteps }: ProgressStepperProps) {
  const currentIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <div className="w-full px-4 py-6">
      <div className="max-w-4xl mx-auto">
        {/* Desktop stepper */}
        <div className="hidden md:flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.includes(step.id);
            const isCurrent = step.id === currentStep;
            const isPast = index < currentIndex;

            return (
              <div key={step.id} className="flex items-center flex-1">
                {/* Step circle */}
                <div className="flex flex-col items-center relative">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all
                      ${isCurrent
                        ? 'bg-brand text-white ring-4 ring-brand/20'
                        : isCompleted || isPast
                        ? 'bg-brand text-white'
                        : 'bg-gray-200 text-gray-600'
                      }
                    `}
                  >
                    {isCompleted || isPast ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>

                  {/* Label */}
                  <span
                    className={`
                      mt-2 text-sm font-medium whitespace-nowrap
                      ${isCurrent ? 'text-brand' : 'text-gray-600'}
                    `}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connecting line */}
                {index < steps.length - 1 && (
                  <div
                    className={`
                      flex-1 h-1 mx-4 rounded transition-all
                      ${isPast || isCompleted
                        ? 'bg-brand'
                        : 'bg-gray-200'
                      }
                    `}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile stepper */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {currentIndex + 1} of {steps.length}
            </span>
            <span className="text-sm font-medium text-brand">
              {steps[currentIndex]?.label}
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-between mt-3">
            {steps.map((step, index) => {
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = step.id === currentStep;
              const isPast = index < currentIndex;

              return (
                <div
                  key={step.id}
                  className={`
                    w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
                    ${isCurrent
                      ? 'bg-brand text-white'
                      : isCompleted || isPast
                      ? 'bg-brand text-white'
                      : 'bg-gray-200 text-gray-600'
                    }
                  `}
                >
                  {isCompleted || isPast ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Default steps for PouchPay payment flow
export const PAYMENT_STEPS: Step[] = [
  { id: 'amount', label: 'Amount' },
  { id: 'wallet', label: 'Wallet' },
  { id: 'auth', label: 'Verify' },
  { id: 'kyc', label: 'ID Check' },
  { id: 'review', label: 'Review' },
  { id: 'instructions', label: 'Pay' },
  { id: 'status', label: 'Complete' }
];
